import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Ledger checkbox — a REAL `<input type="checkbox">` (form semantics,
 * keyboard, screen readers for free) drawn as a 16px box: hairline border,
 * solid pine fill when checked, the shared 2px brand focus ring. Pass
 * `label` (with an `id`) to wire an adjacent `<label htmlFor>` so the text
 * is part of the hit target; `description` adds a mute supporting line.
 */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text wired via htmlFor — requires `id`. */
  label?: ReactNode
  /** Supporting line under the label. */
  description?: ReactNode
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, id, ...props }, ref) => {
    const box = (
      <span className={cn('relative inline-flex size-4 shrink-0', label && 'mt-0.5')}>
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className={cn(
            'peer size-4 cursor-pointer appearance-none rounded-xs border border-rule bg-card transition-colors duration-[180ms] ease-editorial',
            'checked:border-brand checked:bg-brand',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
            'disabled:cursor-not-allowed disabled:border-ruleSoft disabled:bg-paper',
            className,
          )}
          {...props}
        />
        <Check
          aria-hidden="true"
          strokeWidth={3}
          className="pointer-events-none absolute inset-0 m-auto size-3 text-card opacity-0 transition-opacity duration-[120ms] ease-editorial peer-checked:opacity-100"
        />
      </span>
    )

    if (!label) return box

    return (
      <div className="flex items-start gap-2.5">
        {box}
        <span className="min-w-0">
          <label htmlFor={id} className="block cursor-pointer text-sm leading-snug text-ink">
            {label}
          </label>
          {description ? (
            <span className="mt-0.5 block text-xs leading-relaxed text-mute">{description}</span>
          ) : null}
        </span>
      </div>
    )
  },
)
Checkbox.displayName = 'Checkbox'
